// RC2 Sprint 0 · Reachability — Exports List
// Surfaces every plan-export job the user has access to. Reuses the
// existing per-project list endpoint. NO new functionality.
import React, { useEffect, useState, useCallback } from "react";
import { useParams, NavLink } from "react-router-dom";
import {
  Package, Download, RefreshCw, Printer, Clock, CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";

const STATUS_COLOR = {
  pending:    "text-[var(--fop-text-3)]",
  running:    "text-orange-400",
  completed:  "text-emerald-500",
  failed:     "text-red-500",
};

export default function ExportsList({ scope = "global" } = {}) {
  const { projectId } = useParams();
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (scope !== "global") return;
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data.projects || data || []);
      } catch { setProjects([]); }
    })();
  }, [scope]);

  const fetchScoped = useCallback(async (pid) => {
    try {
      const { data } = await api.get(`/projects/${pid}/plan-exports`);
      return (data.items || data.exports || data || []).map((j) =>
        ({ ...j, _project_id: pid }));
    } catch { return []; }
  }, []);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        if (scope === "project" && projectId) {
          setItems(await fetchScoped(projectId));
        } else if (scope === "global") {
          if (!projects.length) { setItems([]); return; }
          const all = await Promise.all(projects.slice(0, 25).map((p) => fetchScoped(p.id)));
          setItems(all.flat().sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))));
        }
      } finally { setLoading(false); }
    })();
  }, [scope, projectId, projects, fetchScoped]);

  const download = (job) => {
    const url = `/api/plan-exports/${job.id}/file`;
    window.open(url, "_blank");
  };

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  return (
    <div className="min-h-screen bg-[var(--fop-bg,#0a0a0b)] text-[var(--fop-text)]" data-testid="exports-list">
      <style>{`@media print {
        body, [data-testid="exports-list"] { background: white !important; color: black !important; }
        [data-no-print] { display: none !important; }
      }`}</style>
      <div className="max-w-5xl mx-auto px-6 py-8">
        <header className="mb-6 flex items-start justify-between gap-4" data-no-print>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
              {scope === "project" ? "Project · Exports" : "ForgedOps · Exports Center"}
            </div>
            <h1 className="text-2xl font-semibold mt-1">Plan export packages</h1>
            <div className="text-[12px] text-[var(--fop-text-3)] mt-1">{items.length} job{items.length === 1 ? "" : "s"}</div>
          </div>
          <button
            onClick={() => window.print()}
            data-testid="exports-print-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[var(--fop-primary)]/40 text-[12px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:bg-[var(--fop-primary)]/10"
          >
            <Printer size={14} /> Print
          </button>
        </header>

        <div className="border border-[var(--fop-border)] rounded-md bg-[var(--fop-surface)] overflow-hidden">
          {loading && (
            <div className="px-4 py-8 text-center text-[var(--fop-text-3)] text-sm" data-testid="exports-loading">Loading exports…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="px-6 py-12 text-center" data-testid="exports-empty">
              <Package size={28} className="mx-auto text-[var(--fop-text-3)] mb-2" />
              <div className="text-sm text-[var(--fop-text-2)]">No exports yet.</div>
              <div className="text-[11px] uppercase tracking-wider font-mono text-[var(--fop-text-3)] mt-2">
                Generate one from any project's plan room.
              </div>
            </div>
          )}
          {items.map((j) => (
            <div
              key={j.id}
              className="px-4 py-3 border-b border-[var(--fop-border)] last:border-b-0 flex gap-3 items-center"
              data-testid={`export-row-${j.id}`}
            >
              <Package size={14} className={STATUS_COLOR[j.status] || "text-[var(--fop-text-2)]"} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {j.template || j.template_id || "Plan export"}
                </div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5">
                  {scope === "global" && (
                    <>
                      {projectName(j._project_id || j.project_id)} ·{" "}
                    </>
                  )}
                  {j.status} · {j.page_count || 0} pages · {String(j.created_at || "").slice(0, 16).replace("T", " ")}
                </div>
              </div>
              {j.status === "completed" && (
                <button
                  onClick={() => download(j)}
                  data-testid={`export-download-${j.id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-[var(--fop-primary)]/40 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:bg-[var(--fop-primary)]/10"
                  data-no-print
                >
                  <Download size={11} /> Download
                </button>
              )}
              {j.status === "failed" && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-red-500/40 bg-red-500/10 text-[11px] font-mono uppercase tracking-wider text-red-400">
                  <AlertTriangle size={11} /> Failed
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
